<script setup>
// Messello onboarding checklist (EPIC, product idea #2). Shown on the empty-state
// onboarding view for a new account: a few high-value setup steps with live
// completion derived from real account data, dismissible (persisted per user).
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import NextButton from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const router = useRouter();
const { uiSettings, updateUISettings } = useUISettings();
const { accountId, currentAccount } = useAccount();

const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);
const agents = computed(() => getters['agents/getAgents'].value || []);
const account = computed(() => currentAccount.value || {});

const aiEnabled = computed(() => {
  const attrs = account.value.custom_attributes || {};
  return !!(
    attrs.ai_auto_suggest ||
    attrs.ai_auto_triage ||
    (attrs.ai_auto_reply_inboxes || []).length
  );
});

const steps = computed(() => [
  {
    key: 'channel',
    done: inboxes.value.length > 0,
    route: 'settings_inbox_new',
    title: 'Connect a channel',
    description: 'Bring your website, WhatsApp, Telegram or email into one inbox.',
  },
  {
    key: 'ai',
    done: aiEnabled.value,
    route: 'general_settings_index',
    title: 'Turn on AI Assist',
    description:
      'Let AI draft replies, auto-tag conversations and answer from your help center.',
  },
  {
    key: 'team',
    done: agents.value.length > 1,
    route: 'agent_list',
    title: 'Invite your team',
    description: 'Add teammates so conversations can be assigned and shared.',
  },
]);

const completed = computed(() => steps.value.filter(step => step.done).length);
const allDone = computed(() => completed.value === steps.value.length);
const visible = computed(
  () => !uiSettings.value?.messello_onboarding_dismissed && !allDone.value
);

const go = route =>
  router.push({ name: route, params: { accountId: accountId.value } });
const dismiss = () =>
  updateUISettings({ messello_onboarding_dismissed: true });
</script>

<template>
  <div
    v-if="visible"
    class="col-span-full rounded-xl border border-n-weak bg-n-solid-2 p-5"
  >
    <div class="flex items-start justify-between gap-3 mb-4">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          Get started with Messello
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ completed }} of {{ steps.length }} done — finish setup to get the
          most out of your inbox.
        </p>
      </div>
      <button
        class="text-sm text-n-slate-10 hover:text-n-slate-12"
        @click="dismiss"
      >
        Dismiss
      </button>
    </div>
    <ul class="flex flex-col gap-3">
      <li
        v-for="step in steps"
        :key="step.key"
        class="flex items-center gap-3"
      >
        <span
          class="flex items-center justify-center w-6 h-6 rounded-full text-xs shrink-0 border"
          :class="
            step.done
              ? 'bg-n-brand text-white border-transparent'
              : 'border-n-weak text-n-slate-10'
          "
        >
          {{ step.done ? '✓' : '' }}
        </span>
        <div class="flex-1 min-w-0">
          <div
            class="text-sm font-medium"
            :class="step.done ? 'text-n-slate-11 line-through' : 'text-n-slate-12'"
          >
            {{ step.title }}
          </div>
          <div class="text-xs text-n-slate-11">{{ step.description }}</div>
        </div>
        <NextButton
          v-if="!step.done"
          faded
          sm
          label="Set up"
          @click="go(step.route)"
        />
      </li>
    </ul>
  </div>
</template>
